import React, { useState } from "react";
import {
  Table,
  Tag,
  Space,
  Input,
  Button,
  Modal,
  Typography,
  Spin,
  Select,
  Switch,
  Descriptions,
  message,
} from "antd";
import {
  SearchOutlined,
  ShopOutlined,
  EditOutlined,
  HistoryOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  saasControlService,
  Tenant,
  UserSession,
  SubscriptionTier,
  SubscriptionStatus,
} from "~/services/saas-control";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";

dayjs.extend(relativeTime);
dayjs.extend(duration);

const { Text } = Typography;
const { Option } = Select;

const tierColors: Record<string, string> = {
  free: "default",
  basic: "blue",
  premium: "gold",
  enterprise: "purple",
};

const statusColors: Record<string, string> = {
  active: "green",
  trial: "blue",
  cancelled: "red",
  expired: "orange",
};

export const TenantsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sessionsModal, setSessionsModal] = useState<{ tenantId: string; tenantName: string } | null>(null);
  const [editModal, setEditModal] = useState<Tenant | null>(null);
  const [editTier, setEditTier] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("");
  const [editActive, setEditActive] = useState(true);

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ["saas-tenants"],
    queryFn: saasControlService.getTenants,
  });

  const { data: tenantSessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["saas-tenant-sessions", sessionsModal?.tenantId],
    queryFn: () => saasControlService.getTenantSessions(sessionsModal!.tenantId),
    enabled: !!sessionsModal,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      saasControlService.updateTenant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saas-tenants"] });
      message.success("Tenant updated");
      setEditModal(null);
    },
    onError: () => message.error("Failed to update tenant"),
  });

  const handleEdit = (tenant: Tenant) => {
    setEditTier(tenant.subscriptionTier);
    setEditStatus(tenant.subscriptionStatus);
    setEditActive(tenant.isActive);
    setEditModal(tenant);
  };

  const handleSaveEdit = () => {
    if (!editModal) return;
    updateMutation.mutate({
      id: editModal.id,
      data: {
        subscriptionTier: editTier,
        subscriptionStatus: editStatus,
        isActive: editActive,
      },
    });
  };

  const formatDuration = (loginAt: string, logoutAt: string | null) => {
    const start = dayjs(loginAt);
    const end = logoutAt ? dayjs(logoutAt) : dayjs();
    const diff = end.diff(start);
    const dur = dayjs.duration(diff);
    if (dur.asMinutes() < 60) return `${Math.round(dur.asMinutes())} min`;
    if (dur.asHours() < 24) return `${Math.round(dur.asHours())} hrs`;
    return `${Math.round(dur.asDays())} days`;
  };

  const columns: ColumnsType<Tenant> = [
    {
      title: "Organization",
      key: "name",
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          <div>
            <div className="font-medium">{record.name}</div>
            <Text type="secondary" className="text-xs">
              /{record.slug}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Status",
      key: "isActive",
      render: (_, record) => (
        <Tag color={record.isActive ? "green" : "red"}>
          {record.isActive ? "ACTIVE" : "DISABLED"}
        </Tag>
      ),
    },
    {
      title: "Tier",
      dataIndex: "subscriptionTier",
      key: "subscriptionTier",
      render: (tier: string) => (
        <Tag color={tierColors[tier]}>{tier.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Subscription",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      render: (status: string) => (
        <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Users",
      dataIndex: "userCount",
      key: "userCount",
      render: (count: number) => (
        <Space>
          <UserOutlined />
          {count}
        </Space>
      ),
      sorter: (a, b) => a.userCount - b.userCount,
    },
    {
      title: "Active Sessions",
      dataIndex: "activeSessionsCount",
      key: "activeSessionsCount",
      render: (count: number) => (
        <Tag color={count > 0 ? "green" : "default"}>{count}</Tag>
      ),
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      key: "lastActivityAt",
      render: (date: string | null) =>
        date ? (
          <Text type="secondary">{dayjs(date).fromNow()}</Text>
        ) : (
          <Text type="secondary">Never</Text>
        ),
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("MMM D, YYYY"),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() =>
              setSessionsModal({
                tenantId: record.id,
                tenantName: record.name,
              })
            }
          >
            Sessions
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const sessionColumns: ColumnsType<UserSession> = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div>
          <div>{record.user?.username || "Deleted user"}</div>
          <Text type="secondary" className="text-xs">
            {record.user?.role} &middot; {record.user?.email || "No email"}
          </Text>
        </div>
      ),
    },
    {
      title: "Login",
      dataIndex: "loginAt",
      key: "loginAt",
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format("MMM D h:mm A")}</div>
          <Text type="secondary" className="text-xs">
            {dayjs(date).fromNow()}
          </Text>
        </div>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) => (
        <Space>
          <ClockCircleOutlined />
          {formatDuration(record.loginAt, record.logoutAt)}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? "Active" : "Ended"}
        </Tag>
      ),
    },
    {
      title: "IP",
      dataIndex: "ipAddress",
      key: "ipAddress",
      render: (ip: string | null) => (
        <Text type="secondary" className="font-mono text-xs">
          {ip || "Unknown"}
        </Text>
      ),
    },
  ];

  const filteredTenants = tenants.filter((tenant) => {
    const searchLower = search.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(searchLower) ||
      tenant.slug.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex gap-4">
        <Input
          placeholder="Search tenants..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredTenants}
        rowKey="id"
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `${total} tenants`,
        }}
      />

      {/* Tenant Sessions Modal */}
      <Modal
        title={`Sessions — ${sessionsModal?.tenantName}`}
        open={!!sessionsModal}
        onCancel={() => setSessionsModal(null)}
        footer={null}
        width={800}
      >
        <Table
          columns={sessionColumns}
          dataSource={tenantSessions}
          rowKey="id"
          loading={sessionsLoading}
          pagination={{ defaultPageSize: 10 }}
          size="small"
        />
      </Modal>

      {/* Edit Tenant Modal */}
      <Modal
        title={`Edit Tenant — ${editModal?.name}`}
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleSaveEdit}
        confirmLoading={updateMutation.isPending}
        okText="Save"
      >
        {editModal && (
          <Descriptions column={1} className="mb-4" size="small" bordered>
            <Descriptions.Item label="Organization">
              {editModal.name}
            </Descriptions.Item>
            <Descriptions.Item label="Slug">
              /{editModal.slug}
            </Descriptions.Item>
          </Descriptions>
        )}
        <div className="space-y-4">
          <div>
            <Text strong className="block mb-1">Subscription Tier</Text>
            <Select
              value={editTier}
              onChange={setEditTier}
              className="w-full"
            >
              {Object.values(SubscriptionTier).map((tier) => (
                <Option key={tier} value={tier}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong className="block mb-1">Subscription Status</Text>
            <Select
              value={editStatus}
              onChange={setEditStatus}
              className="w-full"
            >
              {Object.values(SubscriptionStatus).map((status) => (
                <Option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Text strong>Active:</Text>
            <Switch checked={editActive} onChange={setEditActive} />
          </div>
        </div>
      </Modal>
    </div>
  );
};
